import { useMutation } from '@apollo/client';
import { useCallback } from 'react';

import { getOverlappingActivityAction } from 'common/activeElements/modals/hooks/const';
import useOverlappingBanner from 'common/OverlappingActivitiesBanner/hooks/useOverlappingBanner';
import { standardError } from 'global/commonMessages';
import { notificationStore } from 'global/NotificationStore';

import { CREATE_BREAKDOWN, CREATE_BREAKDOWN_RESPONSE_TYPES } from './gql/createBreakdown';
import {
	createBreakdownErrorNotification,
	flattenBreakdownErrors,
} from './utils/breakdownErrorGenerator';

/**
 * Wraps the `createBreakdown` mutation. Owns the response-type switch:
 *   - Success  → invokes `onSuccess(breakdown)`; clears overlap state.
 *   - Error    → pushes a danger toast via `notificationStore` with the
 *                flattened + labelled error list; clears overlap state.
 *   - Overlaps → stores the overlap list so the caller can render the
 *                shared `<OverlappingBanner>` above Confirm. When the user
 *                ticks "allow to delete overlaps" and resubmits, the
 *                mutation retries with `overlappingActivityAction: 'cancel'`.
 *                If the backend still returns overlaps under that flag,
 *                `cannotCancelOverlaps` flips true (banner switches to the
 *                "cannot cancel" copy and hides the checkbox).
 *
 * The submit helper always forwards the current `allowToDeleteOverlaps`
 * value; callers don't need to pass it. `resetOverlaps` should be wired
 * to every wizard-input change so the banner clears the moment the user
 * edits something (matches `useManageExtraActivity` — the canonical
 * pattern for exactly this response shape).
 *
 * @param {{
 *   onSuccess?: (breakdown: object) => void,
 *   baseTimezone?: string,
 * }} handlers
 */

export const useCreateBreakdown = ({ onSuccess, baseTimezone } = {}) => {
	const [createBreakdownMutation, { loading }] = useMutation(CREATE_BREAKDOWN, {
		fetchPolicy: 'no-cache',
	});

	const {
		overlappingActivities,
		hasOverlappingActivities,
		cannotCancelOverlaps,
		overlappingAction,
		isDelayOptionAvailable,
		allowToDeleteOverlaps,
		allowToDelayOverlaps,
		delayMinutes,
		delayError,
		minDelayMinutes,
		setDelayMinutes,
		setOverlappingAction,
		setOverlappingActivities,
		resetOverlappingActivities,
	} = useOverlappingBanner(baseTimezone);

	const resetOverlaps = useCallback(() => {
		resetOverlappingActivities();
	}, [resetOverlappingActivities]);

	const submit = useCallback(
		async (input) => {
			const overlappingActivityAction = getOverlappingActivityAction({
				allowToDeleteOverlaps,
				allowToDelayOverlaps,
			});

			let data;
			try {
				({ data } = await createBreakdownMutation({
					variables: {
						input,
						overlappingActivityAction,
						delayMinutes: allowToDelayOverlaps ? delayMinutes : null,
					},
				}));
			} catch {
				notificationStore.setNotification(standardError);
				return null;
			}
			const response = data?.createBreakdown;
			if (!response) return null;

			if (response.__typename === CREATE_BREAKDOWN_RESPONSE_TYPES.success) {
				resetOverlaps();
				onSuccess?.(response.breakdown);
			} else if (response.__typename === CREATE_BREAKDOWN_RESPONSE_TYPES.error) {
				const codes = flattenBreakdownErrors(response);
				notificationStore.setNotification(createBreakdownErrorNotification(codes));
				resetOverlaps();
			} else if (response.__typename === CREATE_BREAKDOWN_RESPONSE_TYPES.overlapping) {
				setOverlappingActivities(response.overlappingActivities ?? [], {
					minDelay: response.minDelayMinutes,
					maxDelay: response.maxDelayMinutes,
				});
			}

			return response;
		},
		[
			allowToDeleteOverlaps,
			allowToDelayOverlaps,
			delayMinutes,
			createBreakdownMutation,
			onSuccess,
			resetOverlaps,
			setOverlappingActivities,
		]
	);

	return {
		submit,
		loading,
		overlappingList: overlappingActivities,
		hasOverlappingActivities,
		cannotCancelOverlaps,
		overlappingAction,
		isDelayOptionAvailable,
		allowToDeleteOverlaps,
		allowToDelayOverlaps,
		delayMinutes,
		delayError,
		minDelayMinutes,
		setDelayMinutes,
		setOverlappingAction,
		resetOverlaps,
	};
};
